import { Repository } from "react3l-common";
import { kebabCase } from "lodash";
import { httpConfig } from "config/http";
import { BASE_API_URL } from "config/consts";
import { Observable } from "rxjs";
import { AxiosResponse } from "axios";
import nameof from "ts-nameof.macro";
import { API_ROLL_CALL_PREFIX } from "config/api-consts";
import { Status, StatusFilter } from "models/Status";
import { Subject, SubjectFilter } from "models/Subject";
import { Organization, OrganizationFilter } from "models/Organization";
import { AppUser, AppUserFilter } from "models/AppUser";
import { Semester, SemesterFilter } from "models/Semester";
import { Weekday, WeekdayFilter } from "models/Weekday";
import { ClassShift, ClassShiftFilter } from "models/ClassShift";
import { RollCall, RollCallFilter } from "models/RollCall";
import { CourseSection, CourseSectionFilter } from "models/CourseSection";

export type KeyType = string | number;

export class RollCallRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.baseURL = new URL(API_ROLL_CALL_PREFIX, BASE_API_URL).href;
  }

  public count = (rollCallFilter?: RollCallFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.count)), rollCallFilter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public list = (rollCallFilter?: RollCallFilter): Observable<RollCall[]> => {
    return this.http
      .post<RollCall[]>(kebabCase(nameof(this.list)), rollCallFilter)
      .pipe(Repository.responseMapToList<RollCall>(RollCall));
  };

  public get = (id: number | string): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.get)), { id })
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };

  public getDraft = (filter: RollCallFilter): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.getDraft)), filter)
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };

  public clone = (rollCall: RollCall): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.clone)), rollCall)
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };

  public create = (rollCall: RollCall): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.create)), rollCall)
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };

  public update = (rollCall: RollCall): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.update)), rollCall)
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };

  public delete = (rollCall: RollCall): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.delete)), rollCall)
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };

  public bulkDelete = (idList: KeyType[]): Observable<void> => {
    return this.http
      .post(kebabCase(nameof(this.bulkDelete)), idList)
      .pipe(Repository.responseDataMapper());
  };

  public save = (rollCall: RollCall): Observable<RollCall> => {
    return rollCall.id ? this.update(rollCall) : this.create(rollCall);
  };

  public singleListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.singleListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public filterListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.filterListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public singleListSubject = (
    subjectFilter: SubjectFilter
  ): Observable<Subject[]> => {
    return this.http
      .post<Subject[]>(kebabCase(nameof(this.singleListSubject)), subjectFilter)
      .pipe(Repository.responseMapToList<Subject>(Subject));
  };
  public filterListSubject = (
    subjectFilter: SubjectFilter
  ): Observable<Subject[]> => {
    return this.http
      .post<Subject[]>(kebabCase(nameof(this.filterListSubject)), subjectFilter)
      .pipe(Repository.responseMapToList<Subject>(Subject));
  };

  public singleListAppUser = (
    appUserFilter: AppUserFilter
  ): Observable<AppUser[]> => {
    return this.http
      .post<AppUser[]>(kebabCase(nameof(this.singleListAppUser)), appUserFilter)
      .pipe(Repository.responseMapToList<AppUser>(AppUser));
  };
  public filterListAppUser = (
    appUserFilter: AppUserFilter
  ): Observable<AppUser[]> => {
    return this.http
      .post<AppUser[]>(kebabCase(nameof(this.filterListAppUser)), appUserFilter)
      .pipe(Repository.responseMapToList<AppUser>(AppUser));
  };

  public singleListSemester = (
    semesterFilter: SemesterFilter
  ): Observable<Semester[]> => {
    return this.http
      .post<Semester[]>(
        kebabCase(nameof(this.singleListSemester)),
        semesterFilter
      )
      .pipe(Repository.responseMapToList<Semester>(Semester));
  };
  public filterListSemester = (
    semesterFilter: SemesterFilter
  ): Observable<Semester[]> => {
    return this.http
      .post<Semester[]>(
        kebabCase(nameof(this.filterListSemester)),
        semesterFilter
      )
      .pipe(Repository.responseMapToList<Semester>(Semester));
  };

  public singleListWeekday = (
    weekdayFilter: WeekdayFilter
  ): Observable<Weekday[]> => {
    return this.http
      .post<Weekday[]>(kebabCase(nameof(this.singleListWeekday)), weekdayFilter)
      .pipe(Repository.responseMapToList<Weekday>(Weekday));
  };
  public filterListWeekday = (
    weekdayFilter: WeekdayFilter
  ): Observable<Weekday[]> => {
    return this.http
      .post<Weekday[]>(kebabCase(nameof(this.filterListWeekday)), weekdayFilter)
      .pipe(Repository.responseMapToList<Weekday>(Weekday));
  };

  public singleListClassShift = (
    classShiftFilter: ClassShiftFilter
  ): Observable<ClassShift[]> => {
    return this.http
      .post<ClassShift[]>(
        kebabCase(nameof(this.singleListClassShift)),
        classShiftFilter
      )
      .pipe(Repository.responseMapToList<ClassShift>(ClassShift));
  };
  public filterListClassShift = (
    classShiftFilter: ClassShiftFilter
  ): Observable<ClassShift[]> => {
    return this.http
      .post<ClassShift[]>(
        kebabCase(nameof(this.filterListClassShift)),
        classShiftFilter
      )
      .pipe(Repository.responseMapToList<ClassShift>(ClassShift));
  };

  public singleListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.singleListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };
  public filterListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.filterListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };

  public import = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.import)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public importMapping = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.importMapping)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public export = (filter: any): Observable<AxiosResponse<any>> => {
    return this.http.post("export", filter, {
      responseType: "arraybuffer",
    });
  };

  public exportTemplate = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };

  public exportTemplateMapping = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template-mapping",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };

  // Lớp học phần

  public countCourseSection = (
    courseSectionFilter?: CourseSectionFilter
  ): Observable<number> => {
    return this.http
      .post<number>(
        kebabCase(nameof(this.countCourseSection)),
        courseSectionFilter
      )
      .pipe(Repository.responseDataMapper<number>());
  };

  public listCourseSection = (
    courseSectionFilter?: CourseSectionFilter
  ): Observable<CourseSection[]> => {
    return this.http
      .post<CourseSection[]>(
        kebabCase(nameof(this.listCourseSection)),
        courseSectionFilter
      )
      .pipe(Repository.responseMapToList<CourseSection>(CourseSection));
  };

  public getCourseSection = (id: number | string): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.getCourseSection)), { id })
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };

  public deleteRollCall = (rollCall: RollCall): Observable<RollCall> => {
    return this.http
      .post<RollCall>(kebabCase(nameof(this.deleteRollCall)), rollCall)
      .pipe(Repository.responseMapToModel<RollCall>(RollCall));
  };
}

export const rollCallRepository = new RollCallRepository();
